<template>
  <div
    class="part-pricing-row"
    :class="rowClasses">
    <InfoBox
      :theme="getItemTheme()"
      dense
      class="part-pricing-infobox">
      <!-- Header: Part name and validation -->
      <template #header-title>
        <div class="part-header-title">
          <ValidationIndicator
            v-if="itemValidationErrors.length > 0 || itemValidationWarnings.length > 0"
            :errors="itemValidationErrors"
            :warnings="itemValidationWarnings"
            :show-warnings="true"
            size="sm"
            class="q-mr-xs" />
          <span class="part-name">{{ item.name }}</span>
        </div>
      </template>

      <!-- Header chip: Action badge -->
      <template #header-chip>
        <q-badge
          :color="getBadgeColor()"
          :label="getActionBadge()" />
      </template>

      <!-- Body: Main content -->
      <template #body>
        <div class="pricing-content">
          <!-- Part details section -->
          <div class="part-details-section">
            <div class="part-chips">
              <q-chip
                v-if="item.sku"
                size="sm"
                square
                dense
                color="grey-3"
                text-color="grey-8"
                class="q-ma-none">
                <span class="text-weight-medium">SKU:</span>
                &nbsp;{{ item.sku }}
              </q-chip>
              <q-chip
                v-if="item.oem"
                size="sm"
                square
                dense
                color="grey-3"
                text-color="grey-8"
                class="q-ma-none">
                <span class="text-weight-medium">OEM:</span>
                &nbsp;{{ item.oem }}
              </q-chip>
            </div>
            <div class="supplier-info q-mt-xs">
              <q-icon
                :name="symRoundedAdd"
                size="xs"
                class="q-mr-xs" />
              <span class="text-caption text-grey-7">
                {{ item.isNewPart ? $t('composites.BillPartPricingControl.labels.doesNotExist') : item.supplierName }}
              </span>
            </div>
          </div>

          <!-- Price comparison section -->
          <div class="price-comparison-section">
            <div class="price-grid">
              <!-- Current price -->
              <div class="price-block">
                <div class="price-label text-caption text-grey-7">
                  {{ $t('composites.BillPartPricingControl.labels.currentPrice') }}
                </div>
                <div class="price-value text-h6 text-weight-medium">
                  {{ formatCurrency(item.currentPrice, item.currency) }}
                </div>
                <div
                  v-if="item.lastUpdated"
                  class="price-meta text-caption text-grey-6">
                  <q-icon
                    :name="symRoundedSchedule"
                    size="xs"
                    class="q-mr-xs" />
                  <span>{{ formatRelativeDate(item.lastUpdated) }}</span>
                  <q-tooltip>
                    {{ $t('composites.BillPartPricingControl.tooltips.lastUpdated', { date: formatDate(item.lastUpdated) }) }}
                  </q-tooltip>
                </div>
              </div>

              <!-- Arrow indicator -->
              <div class="price-arrow-block">
                <q-icon
                  :name="priceChangeIcon"
                  :color="priceChangeColor"
                  size="lg" />
              </div>

              <!-- Proposed price -->
              <div class="price-block">
                <div class="price-label text-caption text-grey-7">
                  {{ $t('composites.BillPartPricingControl.labels.proposedPrice') }}
                </div>
                <div class="price-value text-h6 text-weight-medium text-primary">
                  {{ formatCurrency(item.proposedPrice, item.currency) }}
                </div>
                <div class="price-difference text-caption">
                  <span
                    class="text-weight-medium"
                    :class="differenceClasses">
                    {{ formatPriceDifference(item.priceDifference, item.currency) }}
                    ({{ formatPercentage(item.priceDifferencePercent) }})
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action controls section -->
          <div class="action-section">
            <q-btn-toggle
              :model-value="item.action"
              :options="actionOptions"
              :disable="readonly"
              toggle-color="primary"
              spread
              no-caps
              unelevated
              @update:model-value="handleActionChange" />
          </div>
        </div>
      </template>

      <!-- Caption: Warning banner for significant changes -->
      <template #body-caption>
        <q-banner
          v-if="hasSignificantChange && item.action === PricingAction.UPDATE"
          dense
          rounded
          :class="['q-mt-sm', warningBannerClass]">
          <template #avatar>
            <q-icon :name="symRoundedWarning" />
          </template>
          {{ warningMessage }}
        </q-banner>
      </template>
    </InfoBox>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedAdd,
  symRoundedClose,
  symRoundedLock,
  symRoundedSchedule,
  symRoundedTrendingDown,
  symRoundedTrendingFlat,
  symRoundedTrendingUp,
  symRoundedWarning,
} from '@quasar/extras/material-symbols-rounded'
import { date } from 'quasar'
import InfoBox from 'src/CrudManager/Core/composites/Ui/InfoBox/InfoBox.vue'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { PRICING_CONTROL_CONFIG, PricingAction } from '../constants'
import type { BillPartPricingControlContext, PartPricingItem, PricingValidationError } from '../types'
import ValidationIndicator from './ValidationIndicator.vue'

interface Props {
  item: PartPricingItem
  readonly?: boolean
  highlightThreshold?: number
  warningThreshold?: number
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  highlightThreshold: PRICING_CONTROL_CONFIG.MIN_HIGHLIGHT_PERCENTAGE,
  warningThreshold: PRICING_CONTROL_CONFIG.MAX_SAFE_PERCENTAGE,
})

const emit = defineEmits<(event: 'action-change', itemId: string, action: PricingAction) => void>()

// Composables
const { t } = useI18n()
const context = inject<BillPartPricingControlContext>('composites.BillPartPricingControl')

// Computed properties
// Validation
const itemValidationErrors = computed(() => {
  if (context && 'validation' in context) {
    const validation = context.validation as { getItemErrors: (id: string) => PricingValidationError[] }
    return validation.getItemErrors(props.item.id)
  }
  return []
})
const itemValidationWarnings = computed(() => {
  if (context && 'validation' in context) {
    const validation = context.validation as { getItemWarnings: (id: string) => PricingValidationError[] }
    return validation.getItemWarnings(props.item.id)
  }
  return []
})

const rowClasses = computed(() => ({
  'readonly': props.readonly,
  'has-significant-change': hasSignificantChange.value,
  'price-increase': props.item.priceDifference > 0,
  'price-decrease': props.item.priceDifference < 0,
  'action-update': props.item.action === PricingAction.UPDATE,
  'action-keep': props.item.action === PricingAction.KEEP,
  'action-pending': props.item.action === PricingAction.PENDING,
}))

const priceChangeIcon = computed(() => {
  if (props.item.priceDifference > 0) return symRoundedTrendingUp
  if (props.item.priceDifference < 0) return symRoundedTrendingDown
  return symRoundedTrendingFlat
})

const priceChangeColor = computed(() => {
  if (props.item.priceDifference > 0) return 'positive'
  if (props.item.priceDifference < 0) return 'negative'
  return 'grey'
})

const differenceClasses = computed(() => ({
  positive: props.item.priceDifference > 0,
  negative: props.item.priceDifference < 0,
  significant: Math.abs(props.item.priceDifferencePercent) >= props.highlightThreshold,
}))

const hasSignificantChange = computed(() => Math.abs(props.item.priceDifferencePercent) >= props.warningThreshold)

const warningBannerClass = computed(() => ({
  'text-warning': props.item.priceDifference > 0,
  'text-info': props.item.priceDifference < 0,
}))

const warningMessage = computed(() => {
  if (props.item.priceDifference > 0) {
    return t('composites.BillPartPricingControl.warnings.significantIncrease')
  }
  return t('composites.BillPartPricingControl.warnings.significantDecrease')
})

const actionOptions = computed(() => {
  if (props.item.isNewPart) {
    // Different options for new parts
    return [
      {
        label: t('composites.BillPartPricingControl.actions.createNewPart'),
        value: PricingAction.CREATE_NEW_PART,
        icon: symRoundedAdd,
      },
      {
        label: t('composites.BillPartPricingControl.actions.skip'),
        value: PricingAction.SKIP,
        icon: symRoundedClose,
      },
    ]
  }

  // Standard options for existing parts
  const options = [
    {
      label: t('composites.BillPartPricingControl.actions.update'),
      value: PricingAction.UPDATE,
      icon: symRoundedTrendingUp,
    },
    {
      label: t('composites.BillPartPricingControl.actions.keep'),
      value: PricingAction.KEEP,
      icon: symRoundedLock,
    },
  ]

  if (props.item.canCreateSellingOptions && props.item.partId) {
    if (props.item.availableBuyingOptions?.length) {
      options.push({
        label: t('composites.BillPartPricingControl.actions.createLinkedSellingOption'),
        value: PricingAction.CREATE_SELLING_OPTION_LINKED,
        icon: symRoundedAdd,
      })
    }

    options.push({
      label: t('composites.BillPartPricingControl.actions.createIndependentSellingOption'),
      value: PricingAction.CREATE_SELLING_OPTION_INDEPENDENT,
      icon: symRoundedAdd,
    })
  }

  return options
})

// Badge and theme helpers
const getActionBadge = () => {
  switch (props.item.action) {
    case PricingAction.UPDATE:
      return t('composites.BillPartPricingControl.actions.update')
    case PricingAction.KEEP:
      return t('composites.BillPartPricingControl.actions.keep')
    case PricingAction.CREATE_NEW_PART:
      return t('composites.BillPartPricingControl.actions.createNewPart')
    case PricingAction.CREATE_SELLING_OPTION_LINKED:
      return t('composites.BillPartPricingControl.actions.createLinkedSellingOption')
    case PricingAction.CREATE_SELLING_OPTION_INDEPENDENT:
      return t('composites.BillPartPricingControl.actions.createIndependentSellingOption')
    case PricingAction.SKIP:
      return t('composites.BillPartPricingControl.actions.skip')
    default:
      return t('composites.BillPartPricingControl.actions.pending')
  }
}

const getBadgeColor = () => {
  switch (props.item.action) {
    case PricingAction.UPDATE:
      return 'positive'
    case PricingAction.KEEP:
      return 'grey'
    case PricingAction.CREATE_NEW_PART:
      return 'primary'
    case PricingAction.CREATE_SELLING_OPTION_LINKED:
      return 'info'
    case PricingAction.CREATE_SELLING_OPTION_INDEPENDENT:
      return 'info'
    case PricingAction.SKIP:
      return 'negative'
    default:
      return 'warning'
  }
}

const getItemTheme = () => {
  if (props.item.isNewPart) {
    return 'info' // Blue theme for new parts
  }
  if (props.item.action === PricingAction.UPDATE) {
    return props.item.priceDifference > 0 ? 'positive' : 'warning'
  }
  return 'default'
}

// Methods
const formatCurrency = (amount: number, currency = 'CAD'): string => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

const formatPriceDifference = (difference: number, currency = 'CAD'): string => {
  const sign = difference >= 0 ? '+' : ''
  return sign + formatCurrency(difference, currency)
}

const formatPercentage = (percentage: number): string => {
  const sign = percentage >= 0 ? '+' : ''
  return `${sign}${percentage.toFixed(1)}%`
}

const formatDate = (dateString: string): string => {
  return date.formatDate(new Date(dateString), 'YYYY-MM-DD HH:mm')
}

const formatRelativeDate = (dateString: string): string => {
  const now = new Date()
  const targetDate = new Date(dateString)
  const diffInDays = date.getDateDiff(now, targetDate, 'days')

  if (diffInDays === 0) return t('common.today')
  if (diffInDays === 1) return t('common.yesterday')
  if (diffInDays < 7) return t('common.daysAgo', { count: diffInDays })
  if (diffInDays < 30) return t('common.weeksAgo', { count: Math.floor(diffInDays / 7) })
  return date.formatDate(targetDate, 'MMM DD, YYYY')
}

// Event handlers
const handleActionChange = (action: PricingAction) => {
  if (props.readonly) return
  if (action === PricingAction.CREATE_SELLING_OPTION_LINKED && !props.item.availableBuyingOptions?.length) return

  emit('action-change', props.item.id, action)
  context?.setItemAction(props.item.id, action)
}
</script>

<style scoped>
.part-pricing-row {
  transition: all 0.2s ease;
  margin-bottom: 0.75rem;
}

.part-pricing-row.readonly {
  opacity: 0.7;
}

.part-pricing-infobox {
  width: 100%;
}

/* Header */
.part-header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.part-name {
  font-weight: 500;
  word-break: break-word;
}

/* Main content layout */
.pricing-content {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

/* Part details section */
.part-details-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.part-chips {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.supplier-info {
  display: flex;
  align-items: center;
}

/* Price comparison section */
.price-comparison-section {
  padding: 0.5rem 0;
}

.price-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: center;
}

.price-block {
  text-align: center;
}

.price-label {
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.7rem;
}

.price-value {
  margin-bottom: 0.25rem;
  white-space: nowrap;
}

.price-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
}

.price-difference {
  margin-top: 0.25rem;
}

.difference-amount.positive,
.difference-percentage.positive {
  color: var(--q-positive);
}

.difference-amount.negative,
.difference-percentage.negative {
  color: var(--q-negative);
}

.difference-amount.significant,
.difference-percentage.significant {
  font-weight: 600;
}

.price-arrow-block {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
}

/* Action section */
.action-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
}

/* State-based styling */
.part-pricing-row.action-update :deep(.info-box) {
  border-left: 3px solid var(--q-positive);
}

.part-pricing-row.action-keep :deep(.info-box) {
  border-left: 3px solid var(--q-grey-5);
}

.part-pricing-row.action-pending :deep(.info-box) {
  border-left: 3px solid var(--q-warning);
}

/* Warning banner */
.warning-banner {
  margin-top: 0.5rem;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Tablet breakpoint */
@media (max-width: 1024px) {
  .pricing-content {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .price-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .price-arrow-block {
    transform: rotate(90deg);
    padding: 0.25rem 0;
  }

  .part-chips {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

/* Mobile breakpoint */
@media (max-width: 600px) {
  .part-pricing-row {
    margin-bottom: 0.5rem;
  }

  .pricing-content {
    gap: 0.75rem;
  }

  .price-grid {
    gap: 0.5rem;
  }

  .price-value {
    font-size: 1.1rem;
  }

  .action-section :deep(.q-btn-toggle) {
    flex-direction: column;
  }

  .action-section :deep(.q-btn) {
    width: 100%;
  }
}
</style>
